import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { uiStore } from 'shared/store/uiStore'
import { type PageLayoutStore } from 'shared/layout'
import { logger, removeSpacesFromNumericString, toLocalISOString } from 'shared/lib'
import type { ICardFilter } from 'shared/ui'
import { TableStore } from 'shared/ui/Table'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { type RequestCancelAxios, RequestLoadData } from 'shared/api/lib'
import {
  type IClickToText,
  type IClickToTextStatisticsClicks,
  type IClickToTextStatisticsLeads,
} from 'entities/ClickToText'
import { ClickToTextApi } from 'entities/ClickToText/api/clickToText'
import { ClickToText } from 'entities/ClickToText/model/ClickToText'
import { type IParamsGetContactsStatistic } from 'entities/ContactStatistic'
import {
  type IParamsGetContacts,
  type IResponseGetClickToTextStatisticLeads,
  type IResponseStatisticsClicks,
} from 'entities/ClickToText/api/type'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ClickToTextStatisticsClicks } from 'entities/ClickToText/model/ClickToTextStatisticClicks'
import { ClickToTextStatisticsLeads } from 'entities/ClickToText/model/ClickToTextStatisticLeads'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ClickToTextRoutes } from 'pages/clickToText/route/type'

export class ClickToTextDetailsStore {
  private readonly _clickToTextId: string | undefined
  private _clickToText: IClickToText | null = null
  private _isLoading = true
  private _isStatisticsLeadsLoading = false
  private _isStatisticsClicksLoading = false
  private _page = 1
  private _limit = 10
  private _total = 0
  private _search = ''
  private _filter: IParamsGetContactsStatistic['filter'] = 'sent_to'

  private readonly _pageLayoutStore: PageLayoutStore
  private _statisticsLeadsMap: Map<number, IClickToTextStatisticsLeads> = new Map()
  private _contactsDetailsManageStore: ContactsDetailsManageStore
  private _contactStatisticsTableStore = new TableStore<IClickToTextStatisticsLeads>({
    element: 'contact',
    withoutDefaultManageColumns: true,
  })

  private _statisticsClicksMap: Map<string, IClickToTextStatisticsClicks> = new Map()
  private _statisticsClicksTableStore = new TableStore<IClickToTextStatisticsClicks>({
    element: 'clicks',
    withoutDefaultManageColumns: true,
  })
  private _contactsTagsModalStore: ContactsTagsModalStore
  private _calendarFilterStore = new CalendarFilterStore()

  private _disposeFetchData: IReactionDisposer | null = null
  private _disposeChangeParams: IReactionDisposer | null = null

  constructor(
    clickToTextId: string | undefined,
    pageLayoutStore: PageLayoutStore,
    private _entities: IEntitiesStore
  ) {
    this._clickToTextId = clickToTextId
    this._pageLayoutStore = pageLayoutStore
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    this._contactsTagsModalStore = new ContactsTagsModalStore(this._entities)

    makeAutoObservable(this)

    this.reactionFetchData()
    this.reactionChangeParams()
    void this._contactsDetailsManageStore.init()
  }

  reactionChangeParams = () => {
    this._disposeChangeParams?.()
    this._disposeChangeParams = reaction(
      () => this.paramsWithoutPage,
      () => {
        if (this.isLeadsFilter) this._isStatisticsLeadsLoading = true
        if (this.isClicksFilter) this._isStatisticsClicksLoading = true
        this._page = 1
      }
    )
  }

  reactionFetchData = () => {
    this._disposeFetchData?.()
    this._disposeFetchData = reaction(
      () => this.params,
      () => {
        this._requestLoadData.debounceLoadData()
      }
    )
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      if (this.isClicksFilter) await this.fetchStatisticsClicks(cancelRequest)
      if (this.isLeadsFilter) await this.fetchStatisticsLeads(cancelRequest)
    },
  })

  dispose = () => {
    this._disposeFetchData?.()
    this._contactsDetailsManageStore.reset()
    this._disposeChangeParams?.()
    this._requestLoadData.dispose()
  }

  get isLoading() {
    return this._isLoading
  }

  get isStatisticsLeadsLoading() {
    return this._isStatisticsLeadsLoading
  }

  get isStatisticsClicksLoading() {
    return this._isStatisticsClicksLoading
  }

  get clickToText() {
    return this._clickToText
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get params() {
    return {
      page: this._page,
      ...this.paramsWithoutPage,
    }
  }

  get filter() {
    return this._filter
  }

  get search() {
    return this._search
  }

  get total() {
    return this._total
  }

  get page() {
    return this._page
  }

  get limit() {
    return this._limit
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  get calendarFilterStore() {
    return this._calendarFilterStore
  }

  get paramsWithoutPage(): IParamsGetContacts {
    if (this.isClicksFilter) {
      return {
        limit: this._limit,
      }
    }

    return {
      search: removeSpacesFromNumericString(this._search),
      limit: this._limit,
      from: toLocalISOString(this.calendarFilterStore.sendDate?.from ?? null) as string,
      to: toLocalISOString(this.calendarFilterStore.sendDate?.to ?? null) as string,
    }
  }

  get statisticsLeadsItems() {
    return Array.from(this._statisticsLeadsMap.values())
  }

  get statisticsClicksItems() {
    return Array.from(this._statisticsClicksMap.values())
  }

  get statistics() {
    return this._clickToText?.statistics
  }

  get filters() {
    const filters = this.statistics?.getFilters({
      sent_to: {
        tooltipLabel: 'Clicks on the link',
        title: 'Clicks',
      },
      conversions: {
        tooltipLabel: 'Messages received with pre-defined text',
        title: 'Leads',
      },
    })
    const relevantColumnsIds = ['sent_to', 'conversions']

    return filters?.filter((column) => relevantColumnsIds.includes(column.key)) as ICardFilter<
      IParamsGetContactsStatistic['filter']
    >[]
  }

  get contactStatisticsTableStore() {
    return this._contactStatisticsTableStore
  }

  get statisticsClicksTableStore() {
    return this._statisticsClicksTableStore
  }

  get isNoLeadsData() {
    return !this.isStatisticsLeadsLoading && !this._statisticsLeadsMap.size && !this.search
  }

  get isNoClicksData() {
    return !this.isStatisticsClicksLoading && !this._statisticsClicksMap.size
  }

  get contactsTagsModalStore() {
    return this._contactsTagsModalStore
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        this._contactsDetailsManageStore.onOpenContactDetails(
          {
            contactId: data.contactId,
            inboxId: this.clickToText?.inboxId,
          },
          'clickToText',
          true
        )
      },
    }
  }

  get isClicksFilter() {
    return this._filter === 'sent_to'
  }

  get isLeadsFilter() {
    return this._filter === 'conversions'
  }

  init = async () => {
    try {
      const { data } = await ClickToTextApi.getClickToTextById(this._clickToTextId as string)
      this._clickToText = new ClickToText(data, this._entities)
      this.calendarFilterStore.updateCreatedAt(this._clickToText?.createdAt)
      this.calendarFilterStore.setFromFilterDefaultDate()

      void this.fetchStatisticsClicks()
    } catch (e) {
      logger.error(e)

      uiStore.changeRoute({
        path: `/${ClickToTextRoutes.root}`,
      })
    } finally {
      runInAction(() => {
        this._isLoading = false
      })
    }
  }

  fetchStatisticsLeads = async (cancelRequest?: RequestCancelAxios) => {
    if (!this._clickToText) return

    try {
      runInAction(() => {
        this._isStatisticsLeadsLoading = true
      })
      const { data } = await ClickToTextApi.getClickToTextStatisticsLeads(
        this._clickToText.id,
        this.params,
        {
          cancelToken: cancelRequest?.token,
        }
      )

      this.setStatisticsLeadsData(data)
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isStatisticsLeadsLoading = false
      })
    }
  }

  setStatisticsLeadsData = ({ data, meta }: IResponseGetClickToTextStatisticLeads) => {
    this._statisticsLeadsMap.clear()
    this._total = meta.total
    this._page = meta.current_page

    data.forEach((item) => {
      this._statisticsLeadsMap.set(item.id, new ClickToTextStatisticsLeads(item))
    })
  }

  fetchStatisticsClicks = async (cancelRequest?: RequestCancelAxios) => {
    if (!this._clickToText) return

    try {
      runInAction(() => {
        this._isStatisticsClicksLoading = true
      })
      const { data } = await ClickToTextApi.getClickToTextStatisticsClicks(
        this._clickToText.id,
        this.params,
        {
          cancelToken: cancelRequest?.token,
        }
      )

      this.setStatisticsClicksData(data)
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isStatisticsClicksLoading = false
      })
    }
  }

  setStatisticsClicksData = ({ data, meta }: IResponseStatisticsClicks) => {
    this._statisticsClicksMap.clear()
    this._total = meta.total
    this._page = meta.current_page

    data.forEach((clicks) => {
      const id = nanoid()
      this._statisticsClicksMap.set(id, new ClickToTextStatisticsClicks(clicks, id))
    })
  }

  resetSearch = () => {
    this.setSearch('')
  }

  resetFilters = () => {
    this.resetSearch()
    this._calendarFilterStore.resetSearchAndFilters()
  }

  onActiveFilter = (filter: ICardFilter<IParamsGetContactsStatistic['filter']>) => {
    this._filter = filter.key

    this.resetFilters()
  }

  setSearch = (value: string) => {
    this._search = value
  }

  onPaginationTableChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
  }

  onSuccessDelete = () => {
    this.contactStatisticsTableStore.unselectAllIds()
    void this.fetchStatisticsLeads()
  }
}
